// What the pull request page's writes share: the values they take and
// answer with, and `WriteHost`, the part of the store each group of writes
// (pr/draftWrites.ts, pr/prWrites.ts, pr/publish.svelte.ts) works through.
// Every write goes through the store's pending writes (pending.svelte.ts):
// shown at once, sent as an effect of the page's Program (pr/outbox.ts),
// and confirmed by the server's answer or the next load.
import type { WriteError } from '../../api/errors';
import type { ReviewDraft } from '../../api/server';
import type { HandOffStep } from '../../logic/aftermath';
import type { AttentionChange } from '../../logic/attention';
import type { PublishPlan } from '../../logic/inbox';
import type { Snapshot } from '../../logic/timeline';
import { draftCommentId, subject, type PrState, type ReviewEvent, type Subject } from '../optimistic';
import type { Confirm, Writes, WriteSpec } from '../pending.svelte';
import type { Msg } from './program';

export interface NewThread {
  path: string;
  side: 'LEFT' | 'RIGHT';
  line: number | null;
  startLine?: number | null;
  body: string;
  subjectType?: 'LINE' | 'FILE';
}

/** A conversation draft (for a line outside the diff) to hold. */
export interface NewHeld {
  path: string;
  line: number;
  side: 'LEFT' | 'RIGHT';
  commit_sha: string;
  /** The full body as it will be posted (header, text, anchor marker). */
  body: string;
}

/** What publishing did: the review (when one was due) and the conversation drafts posted. */
export interface PublishResult {
  /** Everything was published. */
  ok: boolean;
  /** The review was submitted (false when it failed or none was due). */
  reviewed: boolean;
  /** Conversation drafts that could not be posted (kept). */
  heldFailed: number;
  /** GitHub's time for the last thing the publish wrote, when it said. */
  writtenAt: string | null;
}

/** What a publish changes once its review is in: attention overrides (see `logic/handoff`) and reviewers asked to review again. */
export interface AfterPublish {
  readonly attention: readonly AttentionChange[];
  readonly reviewers: readonly string[];
}

/**
 * How the hand-off runs one of its writes: told why it failed, offering
 * its own Retry (`unlisted`: no failure toast), and for an override, the
 * review's time as its least `basis`.
 */
export interface HandOffWrite {
  onError: (e: WriteError) => void;
  /** Told GitHub's time for what the write did, when it says (a review request's `updated_at`). */
  onTime?: (at: string) => void;
  unlisted: true;
  basis: string | null;
}

/** The hand-off's writes, one per login changed. */
export const stepsOf = (a: AfterPublish): HandOffStep[] => [
  ...a.attention.map((change): HandOffStep => ({ kind: 'attention', change })),
  ...a.reviewers.map((login): HandOffStep => ({ kind: 'review', login })),
];

/** What publishing with a hand-off answers: the publish, and `handOff`, which settles once every hand-off write has. */
export type HandOffResult = PublishResult & { handOff: Promise<void> };

/** The items a draft shows on: its row, and the thread or comment it is in the code. */
export const draftSubjects = (id: ReviewDraft['id']) => [subject.draft(id), subject.thread(draftCommentId(id)), subject.comment(draftCommentId(id))];

/** Unsent text a write sends: the box it came from (its saved-draft key) and what the box held. */
export interface Draft {
  key: string;
  text: string;
}

/** The resources a pending write waits on for confirmation (see `Confirm`). */
export type Reloaded = 'pr' | 'overrides';

/** The store, as its writes see it. */
export interface WriteHost {
  readonly owner: string;
  readonly repo: string;
  readonly number: number;
  /** The pending writes every write runs through. */
  readonly writes: Writes<PrState, Reloaded, Subject>;
  /** The pull request as the server last sent it, and as shown (the pending writes laid over it). */
  readonly server: PrState;
  readonly shown: PrState;
  /** The drafts in the states they are shown in. */
  readonly shownDrafts: ReviewDraft[];
  readonly viewer: string;
  readonly snapshots: Snapshot[];
  /** Head SHA of the diff on screen, while the Files view shows one. */
  readonly diffHead: string | null;
  /** The viewer's pending review on GitHub, if there is one. */
  readonly pendingReviewId: string | null;
  /** Whether the changelist server keeps data here, and why not. */
  readonly serverData: boolean;
  readonly serverDataText: string | null;
  /** The store stopped (the viewer lost access, or no view holds it). */
  readonly disposed: boolean;
  /** Drafts dropped so far, and reads of the overrides started: what a write records when it starts or is accepted. */
  readonly drops: number;
  /** Answers to draft writes that have reached the drafts so far (see the Program's `draftWrites`). */
  readonly draftWrites: number;
  readonly overridesReads: number;
  /** Confirmed by the pull request's next load. */
  readonly byPrLoad: Confirm<Reloaded, unknown>;
  /** A write confirmed by the pull request's next load. */
  write<T = void>(spec: Omit<WriteSpec<PrState, Reloaded, Subject, T>, 'confirm'>): Promise<boolean>;
  /** An ID for an item shown before the server has given it one. */
  tempId(): string;
  /** The viewer as the author of a comment shown before it is saved. */
  me(): { login: string; avatarUrl?: string } | null;
  /** The head a draft is included on now, and the commit new comments anchor to. */
  head(): string | null;
  commentCommit(): string;
  planFor(event: ReviewEvent, body: string, drafts?: readonly ReviewDraft[]): PublishPlan;
  latestEventAt(): string;
  /** The server knows no installation covering the repository. */
  notInstalled(): void;
  /** Tell the page's Program (a draft's answer, the drafts after a mirror). */
  send(m: Msg): void;
  landDraft(draft: ReviewDraft, since: number): void;
  dropDrafts(ids: readonly ReviewDraft['id'][]): void;
  load(): Promise<void>;
  loadDrafts(): Promise<void>;
  loadOverrides(): Promise<void>;
  /** Send the review marks waiting to be saved. */
  flushMarks(): Promise<void>;
}
